/* eslint-disable no-console */
import type { PropsWithChildren } from "react";
import React, { useState, useEffect } from "react";

import * as RealMsalReact from "@azure/msal-react";

import { isDev } from "@/dev";

import { heatAuthService } from "./heat-auth-service";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    StubPublicClientApplication,
} from "./msal-browser";

import type { AccountInfo } from "./msal-browser";

/**
 * Mode can be: 'msal' | 'heatAuth' | 'localOnly'
 * - 'msal': Use real MSAL
 * - 'heatAuth': Use Heat Auth stubs
 * - 'localOnly': Minimal local auth or none
 */
const MODE: "msal" | "heatAuth" | "localOnly" =
    (process.env.REACT_APP_AUTH_MODE as any) || "heatAuth";

/**
 * Toggle detailed console logs via REACT_APP_AUTH_DEBUG
 * Set to "true" to enable, otherwise disabled
 */
const LOG_ENABLED = process.env.REACT_APP_AUTH_DEBUG === "true";

function debugLog(...args: any[]) {
    if (LOG_ENABLED) {
        console.log(...args);
    }
}

/**
 * Define placeholders for the exports we want:
 *   - MsalProvider
 *   - useMsal
 *   - useIsAuthenticated
 *
 * We'll assign them real or stub implementations below.
 */
export let MsalProvider: React.ComponentType<PropsWithChildren<{ instance: any }>>;
export let useMsal: () => {
    instance: any;
    accounts: any[];
    inProgress: string;
};
export let useIsAuthenticated: () => boolean;

// ------------------------------------------------------------------
// Real MSAL references (for MODE === 'msal')
// ------------------------------------------------------------------
const RealMsalProvider = RealMsalReact.MsalProvider;
const realUseMsal = RealMsalReact.useMsal;
const realUseIsAuthenticated = RealMsalReact.useIsAuthenticated;

// ------------------------------------------------------------------
// Stub references (for MODE === 'heatAuth' or 'localOnly')
// ------------------------------------------------------------------
/** Our stub context */
interface StubMsalContextProps {
    instance: StubPublicClientApplication | any;
    accounts: AccountInfo[];
    inProgress: string;
}

const StubMsalContext = React.createContext<StubMsalContextProps>({
    instance: new PublicClientApplication({} as any),
    accounts: [],
    inProgress: "none",
});

/** Stub "MsalProvider" component */
const StubMsalProvider: React.FC<PropsWithChildren<{ instance: any }>> = ({
    instance,
    children,
}) => {
    const [accounts, setAccounts] = useState<AccountInfo[]>([]);
    const [inProgress, setInProgress] = useState<string>("none");

    useEffect(() => {
        async function initialize() {
            if (!(instance instanceof StubPublicClientApplication)) {
                debugLog("[StubMsalProvider] Instance is not a StubPublicClientApplication");

                return;
            }

            try {
                setInProgress("login");
                debugLog("[StubMsalProvider] Attempting silent token acquisition");
                const tokenResponse = await instance.acquireTokenSilent({});
                debugLog("[StubMsalProvider] Silent token acquisition successful:", tokenResponse);
                const accessToken = tokenResponse.accessToken;
                console.log(`Access token: ${accessToken}`);
                const tokenData = await heatAuthService.validateToken(
                    heatAuthService.getAccessToken() || "",
                );
                const accountInfo: AccountInfo = {
                    homeAccountId: tokenData.userId,
                    environment: "heatAuth",
                    tenantId: "heatAuthTenant", // Customize as needed
                    username: tokenData.username,
                    localAccountId: tokenData.userId,
                    authorityType: "B2C", // or appropriate value
                    idTokenClaims: {
                        sub: tokenData.userId,
                        preferred_username: tokenData.username,
                        firstName: tokenData.firstName,
                        lastName: tokenData.lastName,
                        role: tokenData.role,
                        email: tokenData.email,
                        // Add other claims as necessary
                    },
                };
                instance.setActiveAccount(accountInfo);
                setAccounts([accountInfo]);
                setInProgress("none");
                debugLog("[StubMsalProvider] Account set and authentication complete");
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    debugLog("[StubMsalProvider] Interaction required:", error.message);
                    setAccounts([]);
                    setInProgress("none");

                    if (isDev) {
                        console.warn(
                            "You are unauthenticated but running in dev mode, please set the appropriate token.",
                        );

                        return;
                    }

                    window.location.href = "/login";
                } else {
                    debugLog("[StubMsalProvider] Silent token acquisition failed:", error);
                    setAccounts([]);
                    setInProgress("none");
                }
            }
        }

        initialize();
    }, [instance]);

    debugLog(
        "[StubMsalProvider] Rendering with accounts:",
        accounts,
        "and inProgress:",
        inProgress,
    );

    return (
        <StubMsalContext.Provider
            value={{
                instance,
                accounts,
                inProgress,
            }}
        >
            {children}
        </StubMsalContext.Provider>
    );
};

/** Stub "useMsal" hook */
const useStubMsalHook = () => {
    debugLog("[useStubMsal] Invoked");

    return React.useContext(StubMsalContext);
};

/** Stub "useIsAuthenticated" hook */
const useStubIsAuthenticatedHook = () => {
    const { accounts } = React.useContext(StubMsalContext);
    const hasToken =
        localStorage.getItem("accessToken") !== null &&
        localStorage.getItem("legacyToken") !== null;
    const isAuthenticated = accounts.length > 0 || hasToken;
    debugLog("[useStubIsAuthenticated] IsAuthenticated:", isAuthenticated);

    return isAuthenticated;
};

// ------------------------------------------------------------------
// Assign which implementations to export based on MODE
// ------------------------------------------------------------------
if (MODE === "msal") {
    debugLog("[msal-react.tsx] Using REAL MSAL (MsalProvider, useMsal, useIsAuthenticated)");
    MsalProvider = RealMsalProvider;
    useMsal = realUseMsal;
    useIsAuthenticated = realUseIsAuthenticated;
} else {
    debugLog(
        "[msal-react.tsx] Using STUB/HeatAuth (StubMsalProvider, useStubMsal, useStubIsAuthenticated)",
    );
    MsalProvider = StubMsalProvider;
    useMsal = useStubMsalHook;
    useIsAuthenticated = useStubIsAuthenticatedHook;
}
